import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { emptyString } from 'constants/emptyConst';
import { get, set } from '@totalsoft/change-tracking-react';
import { damageType } from 'constants/damageType';
import ErrorIcon from '@mui/icons-material/Error';
import { IconButton, Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { ParamsContext } from 'providers/ParamsContext';

const CascoQuotationHeaderComponent = ({ lens, disabled }) => {
  const { t } = useTranslation();

  const { cvsChAmount } = useContext(ParamsContext);

  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const partialDamageDeductibleAmountList = useMemo(() => {
    return (lens.cascoQuotation.deductibleCosts |> get).filter(x => x.damageTypeCode === damageType.DAUNA);
  }, [lens]);

  const totalDamageDeductiblePercentList = useMemo(() => {
    return (lens.cascoQuotation.deductibleCosts |> get).filter(x => x.damageTypeCode === damageType.DAUNA_TOTALA);
  }, [lens]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={t('Offer.Insurance.Convention')}
          value={(lens.cascoQuotation?.conventionInfo?.conventionName |> get) || emptyString}
          disabled
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={t('Offer.Insurance.InsuredAmountWithCurrency', { currency: lens.currencyCode |> get })}
          fullWidth
          disabled
          isNumeric
          value={lens.cascoQuotation.insuredAmount |> get || null}
          inputProps={{ decimalScale: cvsChAmount, thousandSeparator: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={t('Offer.Insurance.QuotationValue', { currency: lens.currencyCode |> get })}
          fullWidth
          disabled
          isNumeric
          value={(lens.cascoQuotation.insuranceAmount |> get) || null}
          inputProps={{ decimalScale: cvsChAmount, thousandSeparator: true }}
          endAdornment={
            (lens.errorMessage |> get) !== null && (
              <IconButton
                id={'cascoComparator_errorMessageBtn'}
                size='small'
                color='dangerNoBackground'
                onClick={() => setShowErrorMessage(!showErrorMessage)}
                tooltip={lens.errorMessage |> get}
              >
                <ErrorIcon />
              </IconButton>
            )
          }
        />
        {showErrorMessage && (
          <Typography variant='caption'>
            <Box fontStyle='italic'>{lens.errorMessage |> get}</Box>
          </Typography>
        )}
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={t('Offer.Insurance.Periodicity')}
          value={(lens.cascoQuotation.periodicity |> get) || null}
          disabled
          isNumeric
          inputProps={{
            decimalScale: 0,
            thousandSeparator: false
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={t('Offer.Insurance.InsuranceDuration')}
          value={(lens.period |> get) || null}
          disabled
          isNumeric
          inputProps={{
            decimalScale: 0,
            thousandSeparator: false
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={t('Offer.Insurance.TotalQuotationValue', { currency: lens.currencyCode |> get })}
          fullWidth
          disabled
          isNumeric
          inputProps={{ decimalScale: cvsChAmount, thousandSeparator: true }}
          value={(lens.cascoQuotation.totalInsuranceAmount |> get) || null}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          id={'cascoInsurance_partialDamageDeductibleAmount_' + (lens.id |> get)}
          fullWidth
          label={
            partialDamageDeductibleAmountList[0]?.currencyCode
              ? t('Offer.Insurance.PartialDamageDeductibleAmountWithCurrency', {
                  currency: partialDamageDeductibleAmountList[0]?.currencyCode
                })
              : t('Offer.Insurance.PartialDamageDeductibleAmount')
          }
          simpleValue
          value={(lens.cascoQuotation.partialDamageDeductibleAmount |> get) || emptyString}
          onChange={value => (lens.cascoQuotation.partialDamageDeductibleAmount |> set)(value)}
          isSearchable={false}
          valueKey='deductibleCostValue'
          labelKey='deductibleCostValue'
          options={partialDamageDeductibleAmountList}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          id={'cascoInsurance_totalDamageDeductiblePercent_' + (lens.id |> get)}
          fullWidth
          label={t('Offer.Insurance.TotalDamageDeductiblePercent')}
          simpleValue
          value={(lens.cascoQuotation.totalDamageDeductiblePercent |> get) || emptyString}
          onChange={value => (lens.cascoQuotation.totalDamageDeductiblePercent |> set)(value)}
          valueKey='deductibleCostValue'
          labelKey='deductibleCostValue'
          options={totalDamageDeductiblePercentList}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

CascoQuotationHeaderComponent.propTypes = {
  lens: PropTypes.object.isRequired,
  disabled: PropTypes.bool.isRequired
};

export default CascoQuotationHeaderComponent;
